import React from "react";
import { Link } from "react-router-dom";
import SectionHeading from "../components/SectionHeading";
import SEO, { videoJsonLd } from "../components/SEO";
import VideoEmbed from "../components/VideoEmbed";
import { WHAT_TO_BRING } from "../data/whatToBring";

const HERO = "https://images.unsplash.com/photo-1632053652571-a6a45052bbbd?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const STEPS = [
  { n: "01", t: "Get in touch", d: "Fill in our short form (or give us a ring). We will be back with you shortly." },
  { n: "02", t: "We sort the cost", d: "The consultation is £300. Any tests are recommended after we’ve seen you, and we tell you the cost first." },
  { n: "03", t: "Your first visit", d: "A full hour with your consultant — relaxed, thorough, and never rushed. Bring a list of your tablets and any recent results." },
  { n: "04", t: "Tests, if needed", d: "We arrange any scans, monitors or blood tests you need, usually at Cambridge University Hospitals." },
  { n: "05", t: "Your plan", d: "A clear plan, written down for you, with a copy sent to your GP. We follow you up for as long as you need us." },
];

const FAQ = [
  { q: "Do I need a GP referral?", a: "No, you don’t. Most of our patients book themselves in directly. If you happen to have a GP letter, that’s great too — it sometimes speeds things up." },
  { q: "How long is the first appointment?", a: "A new consultation is 1 hour with your consultant. Follow-ups are 30 minutes, in person or by telephone." },
  { q: "Do you accept private medical insurance?", a: "No. We only see self-pay patients, so we can’t bill insurers or accept pre-authorisation codes." },
  { q: "How much does it cost?", a: "The new consultation is £300 for 1 hour. Any tests are recommended after we’ve seen you, and we tell you the cost before anything goes ahead." },
  { q: "Where do I come?", a: "We will send you further details in your appointment letter." },
  { q: "Will my GP know what we agreed?", a: "Yes — with your permission, we write a clear letter to your GP after every visit, so your everyday care stays joined up." },
];

export default function PatientInfo() {
  return (
    <div data-testid="patient-info-page">
      <SEO
        title="Patient Information — What to Expect"
        description="A calm, considered patient pathway at Cambridge Cardiovascular Prevention Clinic — from self-referral enquiry to consultation, diagnostics and follow-up."
        path="/patient-info"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "FAQPage",
              mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
            },
            videoJsonLd({
              name: "Understanding and Managing High Blood Pressure",
              description: "A short patient-education guide from Cambridge Cardiovascular Prevention Clinic covering what high blood pressure is, why it matters, and the everyday choices and treatments that can help bring it into a safer range.",
              url: "https://share.synthesia.io/embeds/videos/bb0be6bc-536d-4729-a858-089f2dd9e93b",
              uploadDate: "2026-02-01",
            }),
          ],
        }}
      />
      <section className="relative">
        <div className="absolute inset-0">
          <img src={HERO} alt="Illustrative image of a consultation" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink/80 to-ink/45" />
        </div>
        <div className="relative container-ccc py-28 text-cream-50">
          <div className="eyebrow text-bronze-light">Patient information</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.02] max-w-3xl text-bronze-light drop-shadow-md">What happens at your visit.</h1>
          <p className="mt-6 max-w-xl text-cream-50 text-lg font-medium leading-relaxed">A simple, friendly process — designed so you can focus on you, not on paperwork.</p>
        </div>
      </section>

      <section className="container-ccc py-20">
        <SectionHeading eyebrow="Step by step" title="From hello to a plan — five simple steps." />
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-5 gap-5">
          {STEPS.map((s) => (
            <div key={s.n} className="p-6 bg-white border border-stone-200/60 rounded-md">
              <div className="font-serif text-bronze-dark text-2xl">{s.n}</div>
              <div className="font-serif text-xl mt-2">{s.t}</div>
              <p className="mt-3 text-sm text-ink-muted leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-cream-100 border-y border-bronze-light/30">
        <div className="container-ccc py-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow="Before you come in" title="What to bring." />
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5 text-sm text-ink-muted">
            {WHAT_TO_BRING.map((x) => (
              <div key={x} className="p-5 bg-white border border-stone-200/60 rounded-md">{x}</div>
            ))}
          </div>
        </div>
      </section>

      <section className="container-ccc py-20" data-testid="patient-videos">
        <SectionHeading
          eyebrow="Patient education"
          title="Short video guides."
          subtitle="Consultant-led explainers on the conditions we treat, in plain English. New videos added over time."
        />
        <div className="mt-10 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-8">
            <VideoEmbed
              src="https://share.synthesia.io/embeds/videos/bb0be6bc-536d-4729-a858-089f2dd9e93b"
              title="Understanding and Managing High Blood Pressure"
              heading="Understanding and managing high blood pressure."
              description="A short guide covering what high blood pressure is, why it matters, and the everyday choices and treatments that can help bring it into a safer range."
              testId="video-hypertension-guide"
            />
          </div>
          <aside className="lg:col-span-4 space-y-5">
            <div className="p-6 bg-cream-100 border border-bronze-light/40 rounded-md">
              <div className="eyebrow">Informational only</div>
              <p className="mt-3 text-sm text-ink-muted">
                These videos give you general information about the conditions we treat. They are not a substitute for a consultation and do not replace advice from your own doctor. Any decisions about treatment are made after individual clinical assessment.
              </p>
            </div>
            <div className="p-6 bg-white border border-stone-200/60 rounded-md">
              <div className="eyebrow">Ready to talk?</div>
              <p className="mt-3 text-sm text-ink-muted">
                If anything in the video reflects your situation, our consultants can review it with you in a 1-hour new consultation.
              </p>
              <Link to="/enquiry" data-testid="patient-video-book-cta" className="mt-4 inline-flex btn-primary text-sm">
                Book an appointment
              </Link>
            </div>
          </aside>
        </div>
      </section>

      <section className="container-ccc py-20">
        <SectionHeading eyebrow="Common questions" title="Things people often ask us." />
        <div className="mt-10 divide-y divide-bronze-light/40 border-y border-bronze-light/40">
          {FAQ.map((f) => (
            <details key={f.q} className="group py-5" data-testid={`faq-${f.q.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "").slice(0, 40)}`}>
              <summary className="flex justify-between items-center cursor-pointer font-serif text-xl list-none">
                {f.q}
                <span className="text-bronze-dark text-2xl group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 text-ink-muted text-base leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
        <div className="mt-10">
          <Link data-testid="patient-info-enquiry-cta" to="/enquiry" className="btn-primary">Book an appointment</Link>
        </div>
      </section>
    </div>
  );
}
